import { Field, FieldProps, FormikProps } from "formik";
import AppPanel from "@/features/invoices/order-confirmation-invoices/app-panel";
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Skeleton,
  Text,
} from "@chakra-ui/react";
import React, { useCallback, useMemo } from "react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { toTitle, toUpper } from "@/utils/case";
import AppPriceInput from "@/features/invoices/app-price-input";
import AppCheckBox from "@/features/line-sheet-sets/app-checkbox";
import { ProformaInvoiceFormFields } from "@/features/invoices/proforma-invoices/upload-proforma-invoice-page";
import AppInput from "@/features/ui/app-input";
import AppCreditInvoiceSelector from "@/features/invoices/app-credit-invoice-selector";
import AppTagController from "@/features/invoices/app-tag-controller";
import { ListAllCreditInvoicesRequest } from "@/features/invoices/credit-invoices/hooks/use-list-all-credit-invoices";
import { AppUtils } from "@/features/ui/utils/app-utils";
import {
  formatCreditInvoiceLabel,
  isOtherBoutiqueCredit,
  ProformaCreditContext,
  UsedCreditInvoiceSummary,
} from "@/features/invoices/credit-invoices/credit-invoice.type";
import PriceUtils from "@/features/ui/utils/price-utils";
import {
  isTotalRequiredAmountManual,
  proformaRequiredAmounts,
  resolveTotalRequiredAmount,
} from "@/features/invoices/proforma-invoices/cross-trade-fee";

interface ProformaInvoiceFigureFormControlProps<T> {
  formik: FormikProps<T>;
  isEditing?: boolean;
  isDisabled?: boolean;
}

interface FormFields extends ProformaInvoiceFormFields {}

export default function ProformaInvoiceFigureFormControl<T extends FormFields>({
  formik,
  isEditing,
  isDisabled,
}: ProformaInvoiceFigureFormControlProps<T>) {
  const { t, tTitle, tCurrencyString } = useI18nHelper();
  // 프로포마 금액 통화 — 폼에 없으면 유로
  const currency = formik.values.currency ?? "EUR";

  const total = useMemo(() => {
    const usedCredit = PriceUtils.sum(
      formik.values.usedCreditInvoices.map((cDetail) => cDetail.amount),
      currency
    );
    const usedDeposit = formik.values.usedDeposit;
    // 송금 필요 금액 = 인보이스 총액(담당자 입력) - 차감 크레딧 - 사용 디파짓
    const { amount, requiredAmount } = proformaRequiredAmounts(
      {
        totalAmount: formik.values.totalAmount,
        proformaInvoiceDetailList: formik.values.proformaInvoiceDetailList,
        usedCreditInvoices: formik.values.usedCreditInvoices,
        usedDeposit,
        crossTradeFeeRate: formik.values.crossTradeFeeRate,
      },
      currency
    );

    return {
      amount,
      usedCredit,
      usedDeposit,
      requiredAmount,
    };
  }, [
    formik.values.totalAmount,
    formik.values.proformaInvoiceDetailList,
    formik.values.usedCreditInvoices,
    formik.values.usedDeposit,
    formik.values.crossTradeFeeRate,
    currency,
  ]);

  const handleCreditInvoiceChange = useCallback(
    (next: UsedCreditInvoiceSummary[]) => {
      formik.setValues((prev) => {
        return {
          ...prev,
          usedCreditInvoices: next,
        };
      });
    },
    [formik]
  );

  // 타부티크 크레딧: 바이어 소유인데 이 프로포르마의 부티크(원발행사 포함)도 에이전시도 아닌 곳이 발행한 크레딧.
  // 기본 선택지는 이 부티크 발행분만 보여주고, 체크하면 바이어의 다른 부티크 크레딧도 보여준다.
  const creditContext = useMemo(
    (): ProformaCreditContext => ({
      boutiqueIds: [
        formik.values.issuedBy?.id,
        formik.values.originallyIssuedBy?.id,
      ],
      agencyId: formik.values.issuedThrough?.id,
    }),
    [
      formik.values.issuedBy?.id,
      formik.values.originallyIssuedBy?.id,
      formik.values.issuedThrough?.id,
    ]
  );
  const hasOtherBoutiqueCredit = formik.values.usedCreditInvoices.some(
    (credit) => isOtherBoutiqueCredit(credit, creditContext)
  );
  const usesOtherBoutiqueCredit =
    !!formik.values.usesOtherBoutiqueCredit || hasOtherBoutiqueCredit;
  // 이미 고른 타부티크 크레딧이 있으면 끌 수 없다 — 먼저 그 크레딧을 빼야 한다.
  const otherBoutiqueCreditCheckBox = (
    <AppCheckBox
      size={"small"}
      name={tTitle("use_other_boutique_credit")}
      isChecked={usesOtherBoutiqueCredit}
      isDisabled={isDisabled || hasOtherBoutiqueCredit}
      onChange={(checked) => {
        formik.setFieldValue("usesOtherBoutiqueCredit", checked);
      }}
    />
  );

  if (!formik.values.id) {
    return (
      <AppPanel title={<Skeleton height={"32px"} />}>
        <Grid
          paddingRight={"12px"}
          gridTemplateColumns={"repeat(3, 1fr)"}
          gridTemplateRows={"20px 24px repeat(2, 20px 24px)"}
          gridColumnGap={"8px"}
          gridRowGap={"4px"}
        >
          <Box />
          <Skeleton />
          <Skeleton />

          <Skeleton />
          <Skeleton />
          <Skeleton />

          <Skeleton />
          <Skeleton />
          <Skeleton />

          <Skeleton />
          <Skeleton />
          <Skeleton />

          <Skeleton />
          <Skeleton />
          <Skeleton />

          <Skeleton gridColumn={"1/4"} />
        </Grid>
      </AppPanel>
    );
  }

  return (
    <AppPanel title={tTitle("common:invoice_figures")}>
      <Flex gap={"12px"} flexDirection={"column"}>
        <Flex flexDirection={"column"} gap={"4px"}>
          <Box fontSize={"12px"} gridColumn={"1/-1"}>
            {toUpper(t("deposit"))}
          </Box>
          <Grid
            gridTemplateColumns={"repeat(3, 1fr) 12px"}
            gridColumnGap={"8px"}
            gridRowGap={"4px"}
          >
            <Field name={"expectedRemainingDepositAmount"}>
              {(
                props: FieldProps<
                  FormFields["expectedRemainingDepositAmount"],
                  FormFields
                >
              ) => {
                return (
                  <FormControl
                    display={"flex"}
                    flexDirection={"column"}
                    alignItems={"start"}
                    gap={"4px"}
                  >
                    <FormLabel
                      margin={"0"}
                      padding={"0"}
                      minHeight={"0"}
                      minWidth={"0"}
                      fontSize={"12px"}
                      color={"#444440"}
                    >
                      {toTitle(t("total_remaining"))}:
                    </FormLabel>
                    <AppPriceInput
                      value={props.field.value}
                      isDisabled={isDisabled}
                      isReadOnly={!isEditing}
                    />
                  </FormControl>
                );
              }}
            </Field>

            <Field name={"usedDeposit"}>
              {(props: FieldProps<FormFields["usedDeposit"], FormFields>) => {
                return (
                  <FormControl
                    display={"flex"}
                    flexDirection={"column"}
                    alignItems={"start"}
                    gap={"4px"}
                  >
                    <FormLabel
                      margin={"0"}
                      padding={"0"}
                      minHeight={"0"}
                      minWidth={"0"}
                      fontSize={"12px"}
                      color={"#444440"}
                    >
                      {toTitle(t("total_using"))}:
                    </FormLabel>
                    <AppPriceInput
                      value={props.field.value}
                      onChange={(value) => {
                        props.form.setValues((prev) => {
                          return {
                            ...prev,
                            usedDeposit: value,
                          };
                        });
                      }}
                      isDisabled={
                        isDisabled ||
                        (props.form.values.expectedRemainingDepositAmount
                          ?.value || 0) === 0
                      }
                      isReadOnly={!isEditing}
                    />
                  </FormControl>
                );
              }}
            </Field>

            <Field name={"usedDepositComment"}>
              {(
                props: FieldProps<FormFields["usedDepositComment"], FormFields>
              ) => {
                return (
                  <Box
                    display={"flex"}
                    flexDirection={"column"}
                    justifyContent={"end"}
                  >
                    <AppInput
                      value={props.field.value}
                      placeholder={t("comment")}
                      isReadOnly={!isEditing}
                      onChange={(value) => {
                        props.form.setFieldValue(props.field.name, value);
                      }}
                    />
                  </Box>
                );
              }}
            </Field>
          </Grid>
        </Flex>

        <Flex flexDirection={"column"} gap={"4px"}>
          <Flex alignItems={"center"} justifyContent={"space-between"}>
            <Box fontSize={"12px"}>{toUpper(t("credit"))}</Box>
            {isEditing && otherBoutiqueCreditCheckBox}
          </Flex>
          <Grid
            gridTemplateColumns={"repeat(3, 1fr) 12px"}
            gridTemplateRows={"auto auto"}
            gridColumnGap={"8px"}
            gridRowGap={"8px"}
          >
            <Field name={"usedCreditInvoices"}>
              {(
                props: FieldProps<FormFields["usedCreditInvoices"], FormFields>
              ) => {
                const {
                  issuedBy,
                  orderedBy,
                  issuedThrough,
                  originallyIssuedBy,
                } = props.form.values;
                let request: ListAllCreditInvoicesRequest | undefined =
                  undefined;

                if (
                  issuedThrough &&
                  orderedBy &&
                  (issuedBy || originallyIssuedBy)
                ) {
                  request = {
                    isUsed__eq: false,
                    "issuedToCompany.id__eq": orderedBy.id,
                    // 기본: 부티크(원발행사 포함) 발행 크레딧 + 에이전시 발행 크레딧.
                    // 타부티크 크레딧 사용을 켜면 발행사 제한 없이 이 바이어의 크레딧을 모두 보여준다.
                    ...(usesOtherBoutiqueCredit
                      ? {}
                      : {
                          "issuedByCompany.id__in": AppUtils.filterDefined([
                            issuedBy?.id,
                            originallyIssuedBy?.id,
                            issuedThrough.id,
                          ]),
                        }),
                    "issuedThroughCompany.id__eq": issuedThrough.id,
                  };
                }

                return (
                  <FormControl
                    display={"flex"}
                    flexDirection={"column"}
                    alignItems={"start"}
                    gap={"4px"}
                  >
                    <FormLabel
                      margin={"0"}
                      padding={"0"}
                      minHeight={"0"}
                      minWidth={"0"}
                      fontSize={"12px"}
                      color={"#444440"}
                    >
                      {toTitle(t("using"))}:
                    </FormLabel>
                    <Box alignSelf={"stretch"}>
                      <AppCreditInvoiceSelector
                        width={"100%"}
                        excludeIds={formik.values.usedCreditInvoices.map(
                          (c) => c.id
                        )}
                        request={request}
                        isReadOnly={!isEditing}
                        isDisabled={isDisabled}
                        onSelect={(value) => {
                          // props.form.setValues((prev) => {
                          //   return {
                          //     ...prev,
                          //     usedCreditInvoices: [
                          //       ...prev.usedCreditInvoices,
                          //       value,
                          //     ],
                          //   };
                          // });
                          handleCreditInvoiceChange([
                            ...props.field.value,
                            value,
                          ]);
                        }}
                      />
                    </Box>
                  </FormControl>
                );
              }}
            </Field>

            <Flex
              display={"flex"}
              flexDirection={"column"}
              alignItems={"start"}
              justifyContent={"end"}
              gap={"4px"}
            >
              <Text
                margin={"0"}
                padding={"0"}
                minHeight={"0"}
                minWidth={"0"}
                fontSize={"12px"}
                color={"#444440"}
              >
                {toTitle(t("total_using"))}:
              </Text>
              <AppPriceInput
                isDisabled={isEditing}
                isReadOnly={!isEditing}
                value={PriceUtils.sum(
                  formik.values.usedCreditInvoices.map(
                    (creditInvoice) => creditInvoice.amount
                  ),
                  currency
                )}
              />
            </Flex>

            <Field name={"usedCreditComment"}>
              {(
                props: FieldProps<FormFields["usedCreditComment"], FormFields>
              ) => {
                return (
                  <Box
                    display={"flex"}
                    flexDirection={"column"}
                    justifyContent={"end"}
                  >
                    <AppInput
                      value={props.field.value}
                      isReadOnly={isEditing}
                      placeholder={t("comment")}
                      onChange={(value) => {
                        props.form.setFieldValue(props.field.name, value);
                      }}
                    />
                  </Box>
                );
              }}
            </Field>

            <GridItem gridColumn={"1/-2"} alignSelf={"stretch"}>
              <AppTagController
                values={formik.values.usedCreditInvoices.map((credit) => {
                  return {
                    id: credit.id,
                    name: formatCreditInvoiceLabel(credit, {
                      t,
                      formatAmount: tCurrencyString,
                    }),
                  };
                })}
                isDisabled={!isEditing}
                onChange={(tags) => {
                  handleCreditInvoiceChange(
                    formik.values.usedCreditInvoices.filter((credit) => {
                      return tags.some((tag) => tag.id === credit.id);
                    })
                  );
                }}
              />
            </GridItem>
          </Grid>
        </Flex>

        {/* 브랜드별 금액은 입력하지 않는다(서버가 총액을 연결 오더·OC 비율로 나눠 저장) — 브랜드만 보여 준다 */}
        {formik.values.proformaInvoiceDetailList.length > 0 && (
          <Text fontSize={"12px"} color={"#8A8A86"}>
            {toTitle(t("brand"))}:{" "}
            {Array.from(
              new Set(
                formik.values.proformaInvoiceDetailList
                  .map((pDetail) => pDetail.brand?.name)
                  .filter((name): name is string => !!name)
              )
            ).join(", ")}
          </Text>
        )}

        <Grid
          gridTemplateColumns={"repeat(3, 1fr) 12px"}
          gridColumnGap={"8px"}
          gridRowGap={"4px"}
        >
          <Box></Box>
          <Flex flexDirection={"column"} alignItems={"start"} gap={"4px"}>
            <Text
              margin={"0"}
              padding={"0"}
              minHeight={"0"}
              minWidth={"0"}
              fontSize={"12px"}
              color={"#444440"}
            >
              {toTitle(t("common:invoice_total_amount"))}:
            </Text>
            <Field name={"totalAmount"}>
              {(props: FieldProps<FormFields["totalAmount"], FormFields>) => {
                return (
                  <AppPriceInput
                    value={props.field.value ?? { currency }}
                    currency={currency}
                    onChange={(value) => {
                      props.form.setFieldValue(props.field.name, value);
                    }}
                    isDisabled={isDisabled}
                    isReadOnly={!isEditing}
                  />
                );
              }}
            </Field>
          </Flex>

          <Flex flexDirection={"column"} alignItems={"start"} gap={"4px"}>
            <Text
              margin={"0"}
              padding={"0"}
              minHeight={"0"}
              minWidth={"0"}
              fontSize={"12px"}
              color={"#444440"}
            >
              {toTitle(t("common:total_remittance_required_amount.abbr"))}:
            </Text>
            <Field name={"totalRequiredAmount"}>
              {(
                props: FieldProps<FormFields["totalRequiredAmount"], FormFields>
              ) => {
                const isManual = isTotalRequiredAmountManual(
                  props.field.value,
                  total.requiredAmount
                );
                return (
                  <Flex flexDirection={"column"} width={"100%"} gap={"2px"}>
                    <AppPriceInput
                      value={resolveTotalRequiredAmount(
                        props.field.value,
                        total.requiredAmount
                      )}
                      currency={currency}
                      onChange={(value) => {
                        props.form.setFieldValue(props.field.name, value);
                      }}
                      isDisabled={isDisabled}
                      isReadOnly={!isEditing}
                    />
                    {/* 직접 고친 값이면 자동 계산값을 보여 주고 되돌릴 수 있게 한다 */}
                    {isManual && (
                      <Text fontSize={"11px"} color={"#8A8A86"}>
                        {t("total_remittance_required_amount.auto")}:{" "}
                        {tCurrencyString(total.requiredAmount.value || 0, {
                          type: "SYMBOL",
                          code: currency,
                        })}
                        {isEditing && (
                          <Text
                            as={"span"}
                            marginLeft={"6px"}
                            color={"#1272EF"}
                            cursor={"pointer"}
                            textDecoration={"underline"}
                            onClick={() => {
                              props.form.setFieldValue(props.field.name, undefined);
                            }}
                          >
                            {t("total_remittance_required_amount.reset")}
                          </Text>
                        )}
                      </Text>
                    )}
                  </Flex>
                );
              }}
            </Field>
          </Flex>
        </Grid>
      </Flex>
    </AppPanel>
  );
}
